import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors, radii, shadows, typography } from '../../styles/tokens';
import { StatusBadge, ToolStatus } from './StatusBadge';

export interface ToolCardProps {
  title: string;
  description: string;
  path: string;
  status: ToolStatus;
  icon: React.ReactElement;
  accentColor?: string;
  dense?: boolean;
  /** Renders the card greyed-out and non-interactive. The route still exists. */
  disabled?: boolean;
}

export const ToolCard: React.FC<ToolCardProps> = ({
  title,
  description,
  path,
  status,
  icon,
  accentColor = colors.primary,
  dense = false,
  disabled = false,
}) => {
  const navigate = useNavigate();

  const effectiveColor = disabled ? colors.textSecondary : accentColor;

  return (
    <Card
      elevation={0}
      onClick={disabled ? undefined : () => navigate(path)}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: disabled ? alpha(colors.border, 0.3) : colors.paper,
        border: `1px solid ${colors.border}`,
        borderRadius: `${radii.card}px`,
        boxShadow: shadows.resting,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'box-shadow 150ms ease, border-color 150ms ease, transform 150ms ease',
        ...(disabled ? {} : {
          '&:hover': {
            boxShadow: shadows.hover,
            borderColor: alpha(effectiveColor, 0.4),
            transform: 'translateY(-2px)',
          },
        }),
      }}
    >
      {/* Tinted artwork band — gives every tool a face beyond a bare icon. */}
      <Box
        sx={{
          height: dense ? 56 : 72,
          flexShrink: 0,
          bgcolor: alpha(effectiveColor, 0.12),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: dense ? 2 : 2.5,
        }}
      >
        <Box
          sx={{
            width: dense ? 36 : 44,
            height: dense ? 36 : 44,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: effectiveColor,
            color: '#FDF6EC',
            '& svg': { fontSize: dense ? 18 : 22 },
          }}
        >
          {icon}
        </Box>
        {(!dense || status !== 'Live') && <StatusBadge status={status} size="small" />}
      </Box>

      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: dense ? 2 : 2.5,
          '&:last-child': { pb: dense ? 2 : 2.5 },
        }}
      >
        <Typography
          variant={dense ? 'subtitle2' : 'subtitle1'}
          component="h3"
          sx={{
            fontFamily: typography.fontFamilyDisplay,
            fontWeight: 600,
            color: colors.textPrimary,
            fontSize: dense ? '0.9375rem' : '1.125rem',
            mb: dense ? 0.5 : 0.75,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant={dense ? 'caption' : 'body2'}
          sx={{
            color: colors.textSecondary,
            lineHeight: dense ? 1.5 : 1.6,
            flex: 1,
            mb: dense ? 1 : 1.5,
            display: dense ? '-webkit-box' : undefined,
            WebkitLineClamp: dense ? 3 : undefined,
            WebkitBoxOrient: dense ? 'vertical' : undefined,
            overflow: dense ? 'hidden' : undefined,
          }}
        >
          {description}
        </Typography>

        <Button
          variant="text"
          size="small"
          disabled={disabled}
          endIcon={<ArrowForward fontSize="small" />}
          onClick={disabled ? undefined : () => navigate(path)}
          sx={{
            alignSelf: 'flex-start',
            px: 0,
            color: disabled ? colors.textSecondary : colors.primary,
            fontWeight: 600,
            '&:hover': {
              bgcolor: disabled ? 'transparent' : alpha(colors.primary, 0.06),
            },
          }}
        >
          {disabled || status === 'WIP' ? 'Coming soon' : 'Open tool'}
        </Button>
      </CardContent>
    </Card>
  );
};
